/** @jsxRuntime automatic */
/** @jsxImportSource react */
import { useEffect, useState } from 'react';
import { BookOpen, RefreshCw } from 'lucide-react';
import { api, type SkillRow } from '../lib/api';
import { errorText } from '../lib/errors';
import { Button, Callout, Checkbox, EmptyState, ErrorState, PageHeader, Panel, Stack, StatusChip, Table, Tag, type Column } from '../ui';
import { plural } from '../ui/format';

/**
 * Knowledge › Skills, rebuilt on the kit (overhaul step 4): the authoritative API and vendor reference skills Gyro
 * grounds answers on. Enable or disable and role-scope them centrally; reload re-indexes from disk without a restart.
 * The same calls as before; a failed load is now said, with Try again, instead of being swallowed.
 */
export function Skills() {
  const [skills, setSkills] = useState<SkillRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [reloading, setReloading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<unknown>(null);

  const load = () => api.skills().then((r) => { setSkills(r.skills); setLoadError(null); }).catch((e: unknown) => setLoadError(e)).finally(() => setLoading(false));

  useEffect(() => {
    void load();
  }, []);

  const toggle = async (id: string, enabled: boolean) => {
    setBusy(id);
    setError(null);
    try {
      const r = await api.setSkill(id, { enabled });
      setSkills(r.skills);
    } catch (e) {
      setError(`${id}: ${errorText(e, 'the change was not saved.')}`);
    } finally {
      setBusy(null);
    }
  };

  const reload = async () => {
    setReloading(true);
    setError(null);
    try {
      await api.reloadSkills();
      await load();
    } catch (e) {
      setError(errorText(e, 'The skills were not reloaded.'));
    } finally {
      setReloading(false);
    }
  };

  const columns: Array<Column<SkillRow>> = [
    { key: 'enabled', header: 'Enabled', width: '80px', align: 'center', cell: (s) => <Checkbox aria-label={`${s.id} enabled`} label="" checked={s.enabled} disabled={busy === s.id} onChange={(e) => void toggle(s.id, e.target.checked)} /> },
    { key: 'skill', header: 'Skill', cell: (s) => <span className="ui-cell-title"><Tag>{s.id}</Tag>{s.meta.writesExternalSystems && <StatusChip tone="warn">writes</StatusChip>}</span> },
    { key: 'description', header: 'Description', muted: true, cell: (s) => s.description || s.name },
    { key: 'access', header: 'Access', muted: true, hideOnPhone: true, cell: (s) => (s.restricted ? s.requiredRoles.join(', ') : 'open') },
    { key: 'risk', header: 'Risk', muted: true, hideOnPhone: true, cell: (s) => s.meta.riskLevel ?? '—' },
  ];

  return (
    <>
      <PageHeader
        meta={<span>Authoritative API and vendor reference skills Gyro grounds answers on</span>}
        actions={<Button icon={RefreshCw} busy={reloading} onClick={() => void reload()}>Reload skills</Button>}
      />
      <div className="ui-page">
        <Stack>
          {error && <Callout tone="bad" role="alert">{error}</Callout>}
          {loadError ? (
            <ErrorState title="The skills could not be loaded" error={loadError} onRetry={() => { setLoading(true); void load(); }} retrying={loading} />
          ) : (
            <Panel
              flush
              title={<span className="ui-title-icon"><BookOpen size={16} strokeWidth={1.75} aria-hidden="true" />Loaded skills ({skills.length})</span>}
              description={loading ? 'Loading…' : `${plural(skills.filter((s) => s.enabled).length, 'skill')} enabled`}
              footer={<span>Reload re-indexes the skills from disk without a restart. Role-scoped skills are offered only to people who hold a required role.</span>}
            >
              <Table stacked
                label="Loaded skills"
                columns={columns}
                rows={skills}
                rowKey={(s) => s.id}
                loading={loading}
                skeletonRows={5}
                empty={<EmptyState compact icon={BookOpen} title="No skills loaded">They ship with the service under /skills.</EmptyState>}
              />
            </Panel>
          )}
        </Stack>
      </div>
    </>
  );
}
